/* eslint-disable @typescript-eslint/no-magic-numbers */
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row, Spin, Tag, Tooltip } from 'antd';
import classNames from 'classnames';
import { Header } from 'components/header';
import { StarIcon } from 'components/icons';
import { LevelsCollapse } from 'components/levels-collapse';
import { useSkillLevelDictionary } from 'hooks/use-skill-levels-dictionary';
import { EpTechnologySkill } from 'types/graphql-types';
import styles from './styles.less';

export type TechnologiesFormProps = {
  technologies: EpTechnologySkill[];
};

const HORIZONTAL_GAP = 0;
const VERTICAL_GAP = 6;

const TechTags: FC<TechTagsProps> = ({ techs, level }) => (
  <Row key={level} className={classNames(styles['row'], styles['data-row'])}>
    <Col span={16}>
      <Row gutter={[HORIZONTAL_GAP, VERTICAL_GAP]}>
        {techs.map((tech) => (
          <Col key={tech.name}>
            <Tag>{tech.name}</Tag>
          </Col>
        ))}
      </Row>
    </Col>
    <Col span={8}>
      <div className={styles['level']}>{level}</div>
    </Col>
  </Row>
);

const PrimaryTechnologies: FC<{
  ems?: EpTechnologySkill;
}> = ({ ems }) => (
  <Row
    gutter={[HORIZONTAL_GAP, VERTICAL_GAP]}
    className={classNames(styles['row'], styles['data-row'])}
    align="middle"
  >
    <Col span={16}>
      {ems ? (
        <Tooltip
          placement="bottom"
          title="This is primary technology from EMS."
        >
          <Tag className={styles['ems-primary']}>
            <StarIcon />
            {ems.name}
          </Tag>
        </Tooltip>
      ) : (
        '—'
      )}
    </Col>
    <Col span={8}>
      {ems ? <div className={styles['level']}>{ems.level}</div> : '—'}
    </Col>
  </Row>
);

export const TechnologiesView: FC<TechnologiesFormProps> = ({
  technologies,
}) => {
  const { t } = useTranslation();

  const techs =
    technologies.filter(
      (tech) => tech.desc !== 'PRIMARY_EMS' && tech.desc !== 'PRIMARY_EP',
    ) || [];

  const emsPrimaryTechnology = technologies.find(
    (tech) => tech.desc === 'PRIMARY_EMS',
  );

  const { data: levelData } = useSkillLevelDictionary();
  const levels = levelData?.skillLevelList?.slice().reverse() || [];

  const showDevelopmentTools = techs.length > 0;

  if (!levels?.length) {
    return <Spin />;
  }

  return (
    <Row>
      <Col xs={{ span: 24 }} lg={{ span: 16 }}>
        <Row
          className={classNames(styles['row'], styles['header-row'])}
          align="middle"
        >
          <Col span={16}>
            <Header level={5} className={styles['header']}>
              {t('Primary technology')}
            </Header>
          </Col>
          <Col span={8}>
            <Header level={5} className={styles['header']}>
              {t('Experience')}
            </Header>
          </Col>
        </Row>
        <PrimaryTechnologies ems={emsPrimaryTechnology} />
        {showDevelopmentTools && (
          <>
            <Row
              className={classNames(styles['row'], styles['header-row-second'])}
            >
              <Col span={16}>
                <Header level={5} className={styles['header']}>
                  {t('Development tools')}
                </Header>
              </Col>
              <Col span={8}>
                <Header level={5} className={styles['header']}>
                  {t('Experience')}
                </Header>
              </Col>
            </Row>
            {levels.map((level) => {
              const filteredTechs = techs.filter(
                (tech) => tech.level === level,
              );

              return filteredTechs.length > 0 ? (
                <TechTags techs={filteredTechs} level={level} />
              ) : null;
            })}
          </>
        )}
      </Col>
      <Col xs={{ span: 0 }} lg={{ span: 8 }}>
        <LevelsCollapse openByDefault />
      </Col>
    </Row>
  );
};

type TechTagsProps = {
  techs: EpTechnologySkill[];
  level: string;
};
